import { EventNames } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';

import { TrackingType } from '../../../../Commerce/TrackingType.csharp';
import { PictureProfile } from '../../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../../Resources/Translations';
import { showFatalErrorModal } from '../../../Partials/Header/FatalErrorModal';
import { getFor } from '../../../ReactComponents/API';
import { RecommendationsService } from '../../../Recommendations/ViewModels/RecommendationsService';
import { showRxSessionExpiredModal } from '../../Rx/Modals/RxSessionExpiredModal';
import { CartDrawerSettingsPageViewModel } from '../CartDrawer/Page/CartDrawerSettingsPageViewModel.csharp';
import { CartAccessor } from '../Components/CartAccessor';
import { PersonalizedCartViewModel } from '../PersonalizedCart/PersonalizedCartViewModel.csharp';
import { CartViewModel } from '../ViewModels/CartViewModel.csharp';
import { SimpleAddToCart } from './SimpleAddToCart/SimpleAddToCart';

export interface CartV2Props {
    cartUrl: string;
    cartItemUrl: string;
    couponUrl: string;
    cartSummaryUrl: string;
    pictureProfile: PictureProfile;
    displayCart: boolean;
    cartDrawerSettings: CartDrawerSettingsPageViewModel;
    showButtonLabel: boolean;
    buttonLabel: string;
}

export interface CartV2State {
    isVisible: boolean;
    cart: CartViewModel | null;
    itemsTotalQuantity: number;
    lastUpdatedItem: string;
    isLoaderActive: boolean;
    updateTrigger: number;
}

export class CartV2 extends React.Component<CartV2Props, CartV2State> {
    private readonly quantityBagdeRef = React.createRef<HTMLDivElement>();
    private quantityBagdeAnimationTimeoutRef = React.createRef<ReturnType<typeof setTimeout>>();
    private hideHeaderTimeoutRef = React.createRef<ReturnType<typeof setTimeout>>();
    private hideHeaderAfterAnimationRef = React.createRef<boolean>();
    private letForQuantityInitAnimationRef = React.createRef<boolean>();

    constructor(props: CartV2Props) {
        super(props);

        this.state = {
            isVisible: false,
            cart: null,
            itemsTotalQuantity: 0,
            lastUpdatedItem: '',
            isLoaderActive: false,
            updateTrigger: 0
        };

        this.updateCart = this.updateCart.bind(this);
        this.triggerCartChange = this.triggerCartChange.bind(this);
        this.toggleCart = this.toggleCart.bind(this);
        this.updateTotalQuantity = this.updateTotalQuantity.bind(this);
    }

    componentDidMount(): void {
        window.addEventListener(EventNames.ToggleCart, this.toggleCart);
        window.addEventListener(EventNames.CartChanged, this.triggerCartChange);
        window.addEventListener(EventNames.CartValidated, this.updateCart);
        window.addEventListener(EventNames.ShowUpdatedCart, this.showUpdatedCart);
        window.addEventListener(EventNames.CartTotalQuantityChanged, this.updateTotalQuantity);
    }

    componentWillUnmount() {
        window.removeEventListener(EventNames.ToggleCart, this.toggleCart);
        window.removeEventListener(EventNames.CartChanged, this.triggerCartChange);
        window.removeEventListener(EventNames.CartValidated, this.updateCart);
        window.removeEventListener(EventNames.ShowUpdatedCart, this.showUpdatedCart);
        window.removeEventListener(EventNames.CartTotalQuantityChanged, this.updateTotalQuantity);
        if (this.quantityBagdeAnimationTimeoutRef.current) {
            clearTimeout(this.quantityBagdeAnimationTimeoutRef.current);
        }
        if (this.hideHeaderTimeoutRef.current) {
            clearTimeout(this.hideHeaderTimeoutRef.current);
        }
    }

    componentDidUpdate(prevProps: Readonly<CartV2Props>, prevState: Readonly<CartV2State>): void {
        if (
            !this.isMoreThenMaxAmount() &&
            this.state.itemsTotalQuantity > prevState.itemsTotalQuantity &&
            this.quantityBagdeRef.current &&
            this.letForQuantityInitAnimationRef.current
        ) {
            this.quantityBagdeRef.current.classList.add('CartV2__QuantityBadge--PulsatingAnimation');
            if (!prevState.itemsTotalQuantity) {
                this.quantityBagdeRef.current.classList.add('CartV2__QuantityBadge--InitPulsatingAnimation');
            }
            (this.quantityBagdeAnimationTimeoutRef as React.MutableRefObject<NodeJS.Timeout>).current = setTimeout(() => {
                if (this.quantityBagdeRef.current) {
                    this.quantityBagdeRef.current.classList.remove('CartV2__QuantityBadge--PulsatingAnimation');
                    this.quantityBagdeRef.current.classList.remove('CartV2__QuantityBadge--InitPulsatingAnimation');
                }
            }, 600);

            if (this.hideHeaderAfterAnimationRef.current) {
                (this.hideHeaderTimeoutRef as React.MutableRefObject<NodeJS.Timeout>).current = setTimeout(() => {
                    window.dispatchEvent(new CustomEvent(EventNames.HideHeader));
                    (this.hideHeaderAfterAnimationRef as React.MutableRefObject<boolean>).current = false;
                }, 1200);
            }
        }
    }

    render() {
        const itemsQuantity = this.isMoreThenMaxAmount() ? '99+' : this.state.itemsTotalQuantity;
        return (
            <div className="CartV2">
                <a
                    className={classNames('CartV2__ToggleButton', {
                        'CartV2__ToggleButton--Disabled': !this.props.displayCart,
                        'CartV2__ToggleButton--LabelVisible': this.props.showButtonLabel
                    })}
                    href={this.props.cartSummaryUrl}
                >
                    {this.props.showButtonLabel && (
                        <span className="hideOnMobile" aria-hidden>
                            {this.props.buttonLabel}
                        </span>
                    )}
                    {this.state.itemsTotalQuantity > 0 && (
                        <>
                            <div
                                className={classNames('CartV2__QuantityBadge', this.state.itemsTotalQuantity >= 10 && 'CartV2__QuantityBadge--BigCount')}
                                ref={this.quantityBagdeRef}
                            >
                                {itemsQuantity}
                            </div>
                        </>
                    )}
                    <span className="visuallyHidden">{getTranslation(keys['cartSummary.openCart'])}</span>
                </a>
                {this.state.cart && this.props.displayCart && (
                    <SimpleAddToCart
                        isVisible={this.state.isVisible}
                        closeWidget={() => this.closeCart()}
                        cartPageUrl={this.props.cartSummaryUrl}
                        cart={this.state.cart}
                        lastUpdatedItem={this.state.lastUpdatedItem}
                        updateTrigger={this.state.updateTrigger}
                    />
                )}
            </div>
        );
    }

    private isMoreThenMaxAmount = (): boolean => {
        const maxAmount = 99;

        return this.state.itemsTotalQuantity > maxAmount;
    };

    private validateCart = (cart: CartViewModel) => {
        if (this.state.cart?.id && cart.id !== this.state.cart.id) {
            showFatalErrorModal();
        }

        if (cart?.hasRemovedRxItems) {
            showRxSessionExpiredModal();
        }
    };

    private triggerCartChange($event: CustomEventInit) {
        (this.letForQuantityInitAnimationRef as React.MutableRefObject<boolean>).current = true;
        RecommendationsService.RefreshRecommendationsForWidget(TrackingType.Basket);
        this.updateCart($event);
    }

    private updateCart($event: CustomEventInit) {
        if ($event && $event.detail) {
            const cart = $event.detail.cart as CartViewModel;

            if (cart) {
                this.validateCart(cart);

                CartAccessor.cacheCart(cart);

                this.setState({
                    cart: cart,
                    lastUpdatedItem: $event.detail.lastUpdatedItem,
                    itemsTotalQuantity: cart.totalQuantity,
                    updateTrigger: this.state.updateTrigger + 1
                });
            }
        }
    }

    private updateTotalQuantity($event: CustomEventInit) {
        if ($event && $event.detail) {
            const totalQuantity = $event.detail.totalQuantity as number;

            if (totalQuantity >= 0) {
                this.setState({ itemsTotalQuantity: totalQuantity });
            }
        }
    }

    private showUpdatedCart = async () => {
        const result = await getFor<PersonalizedCartViewModel>(`${this.props.cartUrl}?skipCache=true`);
        (this.letForQuantityInitAnimationRef as React.MutableRefObject<boolean>).current = true;
        this.setState({ cart: result.cart, itemsTotalQuantity: result.cart.totalQuantity });
        const event = new CustomEvent(EventNames.CartChanged, {
            detail: { cart: result.cart, lastUpdatedItem: '' }
        });
        window.dispatchEvent(event);
    };

    private toggleCart($event: CustomEventInit) {
        if ($event && $event.detail && $event.detail.open !== undefined) {
            return this.handleToggleCart($event.detail.open);
        }

        this.handleToggleCart(!this.state.isVisible);
    }

    private handleToggleCart = (isOpen: boolean) => {
        if (isOpen) {
            this.setState({ isVisible: true });

            window.dispatchEvent(new CustomEvent(EventNames.CloseSearch));
            window.dispatchEvent(new CustomEvent(EventNames.CloseNavigation));
        } else {
            this.closeCart();
        }
    };

    private closeCart() {
        this.setState({ isVisible: false, lastUpdatedItem: '' });
    }
}
